import type { ReactNode } from 'react';

import { SiteFooter } from '../components/SiteFooter.js';
import { SiteNav } from '../components/SiteNav.js';
import { Stamp } from '../components/Stamp.js';

/**
 * Changelog público do SmartApply. Item por item, com o motivo da mudança.
 * Atualizada manualmente a cada release. Versão "próxima" descreve o que
 * está em testes locais e ainda não foi para produção.
 */
export function UpdatesPage() {
  return (
    <>
      <SiteNav />
      <main>
        <article className="mx-auto max-w-3xl px-6 pb-24 pt-20 lg:pt-28">
          <header className="mb-12 border-b border-[var(--border-subtle)] pb-8">
            <p className="mb-4 inline-block">
              <Stamp>atualizações</Stamp>
            </p>
            <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[44px]">
              Tudo que mudou e o porquê
              <span className="text-[var(--accent-saffron)]">.</span>
            </h1>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              histórico de versões · atualizado a cada release
            </p>
            <p className="mt-6 max-w-2xl font-serif text-[17px] leading-[1.55] text-[var(--ink-secondary)]">
              Cada versão lista item por item o que mudou na aplicação, com a justificativa técnica
              ou de produto por trás de cada decisão. Sem changelog de IA genérico: quando algo
              entra ou sai daqui, é porque tem motivo concreto.
            </p>
          </header>

          <Version
            label="v0.3"
            status="em preparo"
            statusVariant="suggestion"
            summary="Reescrita profunda da geração de posts (algoritmo LinkedIn 2026), carrossel agora com conteúdo complementar à legenda (prompt v5.2), gerador de slides programático sem chamar IA, parser de PDF cobrindo todas as seções, fidelidade da leitura do PDF (nome, headline, contato, datas), erros de chave BYOK com causa real, e ajustes de UX que eliminam re-upload desnecessário."
          >
            <Category title="Alcance dos posts">
              <Item
                what="Prompt de geração de posts reescrito do zero (v5.1)"
                why="Diagnóstico baseado em pesquisa do algoritmo LinkedIn 2026 (sistema 360Brew) mostrou que a fórmula viral anterior (bullets com →, '99% dos profissionais', 'A maioria faz X', CTA 'Comenta PALAVRA') virou exatamente o que o algoritmo penaliza como AI-generated. Posts gerados estavam tendo 1-2 reações e 70 impressões. O novo prompt prioriza especificidade obscena (número não-redondo, nome próprio, ferramenta com versão), variação real entre drafts (tamanhos diferentes, aberturas diferentes), e save-bait obrigatório (framework com critério, decisão estruturada)."
              />
              <Item
                what="Hashtags voltam (3-5 por draft, calibradas por área)"
                why="Versão intermediária tinha removido hashtags por default baseado em pesquisa que mostra perda de alcance com hashtags genéricas. Mas hashtags específicas de comunidade técnica (ex.: #PromptEngineering, #DireitoTributário, #SaúdeDigital) ainda têm valor pra topic clustering. Cada draft agora vem com mix de 1-2 nicho + 1-2 amplas + 0-1 contextual, calibradas pela área detectada no perfil. Blacklist mantida pra cortar genéricas (#motivation, #tech, #ai)."
              />
              <Item
                what="Checklist pré-publicação em cada DraftCard"
                why="O texto bom morre se não tiver distribuição inicial. Adicionei painel expansível com 5 passos baseados no que move o 360Brew nos primeiros 60 minutos: editar até soar 100% no seu tom (anti-detecção de IA), publicar 8-11h ou 17-19h, avisar 3-5 contatos pra comentar (comentário vale 15x like), comentar em 2-3 posts de outros perfis hoje, responder cada comentário em até 2h."
              />
              <Item
                what="Sugestão de melhor horário pra postar (item do roadmap)"
                why="Item que estava em 'consideração' no roadmap agora aparece como entregue, na forma do passo 02 do checklist pré-publicação (8-11h e 17-19h horário BR, baseado no algoritmo 360Brew em 2026). Versão personalizada por usuário (que analisa o histórico de publicações pra inferir janelas individuais) fica pra futura release, dependente da telemetria de métricas por post."
              />
            </Category>

            <Category title="Carrossel pra LinkedIn (formato document post)">
              <Item
                what="Fim do markdown vazando nos slides (prompt v5.3 + strip defensivo)"
                why="O LLM costuma marcar ênfase com asteriscos duplos (`**texto importante**`) — padrão herdado de treino em markdown. O renderizador dos slides é tipográfico (serif editorial, accent por cor), não interpreta marcação, então os asteriscos apareciam crus no slide ('**Checklist Rápido:** Debugando…'). Duas correções em paralelo: (1) prompt v5.3 com regra explícita proibindo markdown no carouselContent + item no checklist final de verificação; (2) strip defensivo de `**`, `__` e headings `#` no carousel-builder do client, pra cobrir drafts já gerados antes do v5.3 sem reprocessar. Quando o autor quer ênfase real, a heurística do builder já detecta números, siglas em CAIXA ALTA e termos técnicos e aplica o destaque visual em saffron."
              />
              <Item
                what="Carrossel passa a trazer conteúdo complementar à legenda (prompt v5.2)"
                why="No fluxo anterior o carrossel era a própria legenda quebrada em slides. O leitor recebia a mesma informação duas vezes — desperdício do espaço visual. Agora a IA gera, na mesma chamada, um carouselContent paralelo com ângulo COMPLEMENTAR: se a legenda é narrativa, o carrossel é checklist ou framework; se a legenda é argumento, o carrossel é exemplo aplicado; se a legenda é provocação, o carrossel é o 'como fazer na prática'. Mesmo tema, profundidade e estrutura distintas, 600-1.800 chars dividido em 5-8 parágrafos prontos pra virar slides. Drafts antigos (gerados antes do v5.2) caem em fallback automático e continuam usando a legenda — zero quebra retroativa."
              />
              <Item
                what="Gerador de carrossel 100% local (sem chamar IA)"
                why="Versão anterior chamava o LLM pra quebrar o post em slides, gastava tokens e batia em rate limit do Google. Não fazia sentido — o texto já está pronto, é só decomposição editorial. Implementado com heurística determinística no client: split por parágrafos, primeira frase vira título, accent detectado por regex (números não-redondos, palavras em CAIXA ALTA, termos técnicos). Instantâneo, grátis, sem rate limit."
              />
              <Item
                what="Export como ZIP de imagens PNG (1080×1350 cada)"
                why="Subir como múltiplas imagens no LinkedIn gera o carrossel swipável nativo no feed. Mais limpo visualmente que o document post tradicional. Manteve PDF como segunda opção pra quem quer o formato 'X páginas + Baixar documento'."
              />
              <Item
                what="Carrossel abre em nova aba (preserva drafts da aba original)"
                why="No fluxo anterior, gerar carrossel substituía a tela e perdia os outros drafts gerados na sessão. Agora abre nova aba via window.open, mantém intacto o estado da aba original com todos os drafts."
              />
              <Item
                what="Carrossel via URL base64 (sem depender de storage cross-tab)"
                why="Tentativa anterior usava localStorage pra passar o JSON do carrossel entre abas. Algum cenário (StrictMode dev + partitioned storage de alguns browsers) causava 'carrossel não encontrado'. Agora o JSON inteiro vai codificado base64 no hash da URL. Sobrevive a F5, funciona sempre, sem depender de storage."
              />
            </Category>

            <Category title="Análise de perfil">
              <Item
                what="Parser de PDF cobre TODAS as seções do LinkedIn"
                why="Versão anterior parseava só 6 seções fixas (headline, summary, experience, skills top 3, certifications, education). Tudo mais era descartado, mesmo quando o PDF tinha. Agora cobre Featured, Services, Recommendations, Volunteer, Languages, Publications, Projects, Honors & Awards, Courses, Test Scores, Organizations, Skills completas (não só top 3) e seções customizadas. Texto bruto do PDF também vai pro LLM como fallback (até 32k chars) pra não perder o que o parser não reconhecer."
              />
              <Item
                what="4 pilares como rubrica explícita por finding"
                why="Cada finding do diagnóstico agora vem marcado com quais dos 4 pilares ele melhora quando endereçado (views, interactions, visits, ats). UI mostra placar por pilar no topo do diagnóstico. Antes era preciso ler cada finding individualmente pra entender qual objetivo de produto ele endereçava."
              />
              <Item
                what="Fix do erro 502 Bad Gateway na análise"
                why="Gemini 2.5 Flash truncava o JSON no meio porque o output ultrapassava o maxTokens default. Como o JSON da análise completa (com pilares + todas as seções extras) ficou maior, o LLM cortava na 7ª finding e devolvia JSON inválido. Maxtokens elevado pra 16k cobre perfis densos."
              />
            </Category>

            <Category title="Fidelidade da leitura do PDF">
              <Item
                what="Nome real do perfil, mesmo com headline longa quebrada em várias linhas"
                why="Headlines longas quebram em 2-3 linhas no PDF e a última linha (ex.: 'Integração de Sistemas') parece nome próprio. O parser escolhia essa linha como nome, a análise dizia 'nome do perfil genérico' e a headline saía vazia. Agora o slug público (linkedin.com/in/nome-sobrenome) serve de confirmação do nome real, e linhas que continuam uma headline quebrada (linha anterior terminando em conector como pipe, vírgula ou preposição) são descartadas como candidatas a nome. A headline inteira também é reconstruída juntando as linhas."
              />
              <Item
                what="Domínio do e-mail não aparece mais como URL de portfólio"
                why="O e-mail de contato (ex.: fulano@outlook.com) continha o domínio outlook.com, que o extrator de URLs listava como 'portfólio', e a análise cobrava 'URL de portfólio genérica'. E-mails agora saem do texto antes do match de URLs, e a busca fica restrita à seção Contato do PDF (antes, um Scrum.org solto numa certificação também virava portfólio)."
              />
              <Item
                what="Análise recebe a data de hoje (fim do falso 'data de início no futuro')"
                why="O prompt não informava a data atual, então a IA usava a data do próprio treino como referência e marcava início de cargo recente (ex.: junho de 2025, com hoje em julho de 2026) como 'data futura', um falso problema crítico. A data de referência agora vai no prompt, com regra explícita: início anterior ou igual a hoje com fim 'atual' é cargo em andamento válido."
              />
              <Item
                what="IA proibida de apontar 'falta de foto' e afins"
                why="O export PDF do LinkedIn não inclui foto de perfil, banner, endorsements, seguidores nem estado do Open To Work. A análise às vezes tratava essas ausências como problema crítico do perfil. O system prompt agora lista o que o PDF não tem como mostrar e proíbe findings sobre isso; no máximo entra uma nota de verificação manual, como sugestão."
              />
            </Category>

            <Category title="Validação da chave de IA (BYOK)">
              <Item
                what="Erro de chave mostra a causa real: quota, modelo aposentado, chave inválida ou instabilidade"
                why="Quase qualquer falha na validação virava 'provider recusou a chave', inclusive com chave válida: conta OpenAI sem créditos (a OpenAI não dá mais créditos grátis e responde 429 insufficient_quota), modelo aposentado (404) ou erro de rede. A classificação agora usa o código HTTP real da resposta do provider. Sem créditos aponta pro billing, modelo indisponível sugere trocar o modelo na lista, instabilidade pede pra tentar de novo. Erro de provider também deixou de responder HTTP 401, que se confundia com sessão expirada no console do navegador."
              />
              <Item
                what="Catálogo de modelos validado contra a API real do Google"
                why="Gemini 1.5 Pro e 2.0 Flash exp foram aposentados pelo Google; quem os escolhia no dropdown recebia 404 exibido como 'chave recusada'. O catálogo novo foi testado com generateContent numa chave free tier recém-criada: 2.5 Flash-Lite responde 404 'no longer available to new users' e 2.5 Pro estoura cota no free tier, então ficaram de fora. Entram Gemini 3.5 Flash-Lite e 3.6 Flash (200 OK comprovado), com 2.5 Flash seguindo como recomendado. A lista OpenAI ganha GPT-4.1 e GPT-4.1 mini. A copy do onboarding do Gemini também deixou de prometer limites numéricos do free tier, que o Google muda com frequência."
              />
            </Category>

            <Category title="UX e produtividade">
              <Item
                what="Persistência do PDF processado por aba (sessionStorage)"
                why="Navegar entre /análise e /posts perdia o snapshot do PDF e forçava re-upload + re-análise (gastando tokens duas vezes). Agora o LinkedInProfile completo é cacheado em sessionStorage e reenviado como fallback se o servidor perder o snapshot in-memory (restart de container, TTL)."
              />
              <Item
                what="Botão 'Subir novo PDF' na tela de diagnóstico"
                why="Antes o reset estava escondido como link discreto ('analisar outro perfil'). Promovido pra botão sólido no rodapé do diagnóstico, com explicação clara: 'esta análise fica disponível enquanto a aba estiver aberta'."
              />
              <Item
                what="Provider key direto na rota /posts (sem re-upload do PDF)"
                why="Quando a sessão server-side expirava mas o sessionStorage ainda tinha a análise persistida, /posts pedia pra fazer upload do PDF de novo só pra inserir a chave. Não fazia sentido — gastava tokens com análise nova. Agora /posts detecta se o provider está configurado e mostra o ProviderForm inline quando falta a key, sem precisar tocar no upload."
              />
              <Item
                what="Múltiplas publicações por draft set (histórico)"
                why="Um draft set tem 3-5 variantes; o user pode querer publicar 1 por dia ao longo de uma semana. Antes só dava pra marcar 1 como publicado (sobrescrevia). Agora cada draft tem entrada própria no histórico de publicações, com botão 'desfazer' individual por marcação. Métrica de cadência semanal/mensal usa o histórico real."
              />
            </Category>

            <Category title="Modo novo: lançamento de oferta">
              <Item
                what="3ª aba em /posts pra gerar post de lançamento"
                why="Caso de uso explícito não coberto pelos modos 'link' e 'tendência': quando o autor tem uma oferta concreta (template, lista de prompts, checklist, ferramenta) e quer transformar em post de captação. Form coleta público-alvo, oferta, painpoints (dor → solução), outcome em 1 frase e palavra-chave. Backend usa prompt específico que evita o template Yago Martins literal (saturado em 2026) e gera 2-3 variantes adaptadas."
              />
            </Category>

            <Category title="Bug fixes e robustez">
              <Item
                what="Fix do erro 412 'faça upload do PDF' depois do restart do API"
                why="Snapshot do PDF vivia in-memory no server (byokStore) e era perdido em qualquer restart. Mesmo com a análise visível em tela (sessionStorage), o /posts falhava porque o server não tinha mais o profile. Agora o client envia o LinkedInProfile cacheado como fallback no body de /analyze, /posts/* — server reidrata automaticamente."
              />
              <Item
                what="Schema do PostDraft aceita hashtags vazias"
                why="Em alguma versão intermediária, schema obrigava min(1) em hashtags. Drafts gerados sem hashtags falhavam validação Zod. Schema relaxado, hashtags agora opcional (default array vazio)."
              />
            </Category>

            <Category title="Conteúdo do produto">
              <Item
                what="Escopo de público corrigido em prompts e copy"
                why="Algumas referências hardcoded ainda diziam 'assistente técnico de LinkedIn' / 'profissionais técnicos'. O produto é universal — atende qualquer área (advogado, médico, coach, dev, vendedor, professor, RH, executivo). Prompts agora detectam a área pelo perfil e calibram exemplos, vocabulário e hashtags. CLAUDE.md atualizado com os 4 pilares como rubrica."
              />
            </Category>
          </Version>

          <Version
            label="v0.2"
            status="em produção"
            statusVariant="positive"
            summary="Auth completa com Argon2id, 2FA TOTP, persistência de análises, roadmap público com votação, geração de posts (modos link e tendência), cadência semanal. Deploy automático Cloudflare Pages (site e app) + Fly.io (API)."
          >
            <Category title="Estrutura">
              <Item
                what="Auth completa: registro, login, recovery, 2FA TOTP, sessões em cookie httpOnly assinado"
                why="Multi-user a partir da Fase 6 do plano. Senhas com Argon2id (não bcrypt nem scrypt). 2FA opcional via app autenticador. Rate limit por e-mail no login e recovery."
              />
              <Item
                what="Persistência de análises com user_id (opt-in)"
                why="Análises só ficam salvas se o user clicar 'salvar análise'. PDF e API key continuam apenas em memória de sessão, conforme princípio de privacidade do projeto."
              />
              <Item
                what="Roadmap público com votação anônima"
                why="Construir com a comunidade. Items entram em 'consideração' e migram pra 'planejado' quando recebem votos. Sugestões livres passam por revisão técnica antes de virar items."
              />
            </Category>
            <Category title="Geração de posts">
              <Item
                what="Modo 'a partir de link/texto' (curadoria, opinião, notícia, case)"
                why="Quando o user quer reagir a um post existente do feed. Suporte multimodal (imagem do post original) em Anthropic, OpenAI, Google."
              />
              <Item
                what="Modo 'tendências da minha área' (descoberta via web search)"
                why="LLM pesquisa tópicos quentes dos últimos 30-90 dias na área do user e gera drafts. Requer Anthropic ou Google (providers com web search nativo)."
              />
            </Category>
            <Category title="Cadência e métricas">
              <Item
                what="Dashboard semanal com meta de posts"
                why="User define quantos posts por semana quer publicar. App mostra progresso, histórico de análises e drafts gerados em janelas 7d/30d."
              />
            </Category>
            <Category title="Operação">
              <Item
                what="Deploy automático Cloudflare Pages (site + app) + Fly.io (API)"
                why="Push pra main dispara GitHub Actions com filtro de paths. Postgres no Neon (sa-east-1), API no Fly (gru), CDN Cloudflare. Custo total ~$5/mês até validar product-market fit."
              />
              <Item
                what="Auditoria de segurança pré-lançamento"
                why="SSRF protection no fetcher de links (block IPs privados, redirects validados hop-a-hop), headers HSTS preload, X-Frame DENY, Permissions-Policy zerada, drizzle-orm bump pra CVE GHSA-gpj5-g38j-94v9, container API rodando como user não-root."
              />
            </Category>
          </Version>

          <Version
            label="v0.1"
            status="release inicial"
            statusVariant="default"
            summary="MVP do diagnóstico de perfil LinkedIn via upload de PDF. Análise multi-provider (Anthropic, OpenAI, Google, OpenRouter) com BYOK estrito. Sem persistência: tudo em memória de sessão."
          >
            <Category title="Diagnóstico">
              <Item
                what="Análise de perfil contra Recruiter Search, algoritmo do feed e ATS externo"
                why="Output em 5 partes: forças a preservar, problemas críticos (com citação literal + razão fundamentada), sugestões, quick wins de até 30 min, reescritas prontas (headline e summary)."
              />
              <Item
                what="BYOK estrito (Bring Your Own Key)"
                why="A chave de IA pertence ao user. Sem 'modo demo com nossa chave'. Custo da operação fica no provider escolhido pelo user — SmartApply não cobra pela inferência."
              />
            </Category>
          </Version>

          <footer className="mt-16 border-t border-[var(--border-subtle)] pt-6">
            <p className="font-sans text-xs italic text-[var(--ink-tertiary)]">
              Algo importante não está aqui? Mande sugestão de melhoria em{' '}
              <a
                href="/#suggestion"
                className="text-[var(--ink-secondary)] underline underline-offset-4 hover:text-[var(--ink-primary)]"
              >
                construído com você
              </a>{' '}
              ou direto em{' '}
              <a
                href="mailto:contato@smartapply.com.br"
                className="text-[var(--ink-secondary)] underline underline-offset-4 hover:text-[var(--ink-primary)]"
              >
                contato@smartapply.com.br
              </a>
              .
            </p>
          </footer>
        </article>
      </main>
      <SiteFooter />
    </>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

interface VersionProps {
  label: string;
  status: string;
  statusVariant: 'default' | 'positive' | 'suggestion' | 'critical';
  summary: string;
  children: ReactNode;
}

function Version({ label, status, statusVariant, summary, children }: VersionProps) {
  return (
    <section className="mb-16">
      <header className="mb-6 flex flex-wrap items-baseline gap-3 border-b border-[var(--border-default)] pb-3">
        <h2 className="font-serif text-[28px] font-medium leading-none text-[var(--ink-primary)]">
          {label}
        </h2>
        <Stamp variant={statusVariant}>{status}</Stamp>
      </header>
      <p className="mb-8 font-serif text-[16px] leading-[1.55] text-[var(--ink-secondary)]">
        {summary}
      </p>
      <div className="space-y-10">{children}</div>
    </section>
  );
}

interface CategoryProps {
  title: string;
  children: ReactNode;
}

function Category({ title, children }: CategoryProps) {
  return (
    <div>
      <h3 className="mb-4 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {title}
      </h3>
      <ul className="space-y-5">{children}</ul>
    </div>
  );
}

interface ItemProps {
  what: string;
  why: string;
}

function Item({ what, why }: ItemProps) {
  return (
    <li className="border-l-2 border-[var(--accent-saffron)] pl-5">
      <p className="font-serif text-[17px] leading-[1.4] text-[var(--ink-primary)]">{what}</p>
      <p className="mt-2 font-sans text-[14px] leading-[1.55] text-[var(--ink-secondary)]">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          por quê ·{' '}
        </span>
        {why}
      </p>
    </li>
  );
}
